import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2 } from "lucide-react";
import { editComment, deleteComment } from "../../services/api";

const CommentList = ({ comments = [], onCommentUpdated, onCommentDeleted, editCommentAction = editComment, deleteCommentAction = deleteComment }) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const startEdit = (comment) => {
    setError("");
    setConfirmId(null);
    setEditingId(comment.id);
    setDraft(comment.text);
  };

  const saveEdit = async (comment) => {
    if (!draft.trim() || busyId !== null) return;
    setBusyId(comment.id);
    setError("");

    try {
      const response = await editCommentAction(comment.id, draft.trim());
      onCommentUpdated?.(response.data);
      setEditingId(null);
    } catch (err) {
      console.error("Error al modificar comentario:", err);
      setError("No se pudo modificar el comentario.");
    } finally {
      setBusyId(null);
    }
  };

  const removeComment = async (comment) => {
    if (busyId !== null) return;
    setBusyId(comment.id);
    setError("");

    try {
      await deleteCommentAction(comment.id);
      onCommentDeleted?.(comment.id);
      setConfirmId(null);
    } catch (err) {
      console.error("Error al eliminar comentario:", err);
      setError("No se pudo eliminar el comentario.");
    } finally {
      setBusyId(null);
    }
  };

  if (!comments.length) {
    return (
      <p className="py-3 text-base italic text-slate-500">
        {t("comments.no_comments")}
      </p>
    );
  }

  return (
    <div className="mt-4 space-y-3" aria-label={t("comments.list_aria")}>
      {comments.map((comment) => (
        <div
          key={comment.id}
          className="rounded-xl border border-sky-100 bg-sky-50/70 p-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="break-words text-base font-bold text-[#245580]">
                {comment.author_name}
              </p>
              <time className="mt-1 block text-xs text-slate-500">
                {new Date(comment.created_at).toLocaleString()}
              </time>
            </div>

            {comment.is_author && editingId !== comment.id && (
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => startEdit(comment)}
                  aria-label={t("post.item.edit", "Modifier")}
                  title={t("post.item.edit", "Modifier")}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#247BA8] transition hover:bg-sky-100"
                >
                  <Pencil size={17} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    setConfirmId(comment.id);
                  }}
                  aria-label={t("post.item.delete", "Supprimer")}
                  title={t("post.item.delete", "Supprimer")}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-600 transition hover:bg-red-100"
                >
                  <Trash2 size={17} />
                </button>
              </div>
            )}
          </div>

          {editingId === comment.id ? (
            <div className="mt-3 space-y-3">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={3}
                maxLength={5000}
                className="w-full rounded-xl border border-sky-200 bg-white p-3 text-base text-slate-800 outline-none focus:ring-2 focus:ring-[#247BA8]"
                aria-label={t("post.item.edit", "Modifier")}
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => saveEdit(comment)}
                  disabled={!draft.trim() || busyId !== null}
                  className="rounded-lg bg-[#F97316] px-4 py-2 text-base font-semibold text-white disabled:opacity-50"
                >
                  {t("common.save", "Enregistrer")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setError("");
                  }}
                  disabled={busyId !== null}
                  className="rounded-lg bg-white px-4 py-2 text-base text-slate-700"
                >
                  {t("common.cancel", "Annuler")}
                </button>
              </div>
            </div>
          ) : (
            <p className="mt-3 whitespace-pre-wrap break-words text-base leading-7 text-slate-700">
              {comment.text}
            </p>
          )}

          {comment.is_author && confirmId === comment.id && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3">
              <p className="mb-3 text-sm text-red-800">
                {t("post.item.confirm_delete", "Supprimer ce commentaire ?")}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => removeComment(comment)}
                  disabled={busyId !== null}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {t("post.edit_modal.confirm_delete", "Supprimer")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmId(null);
                    setError("");
                  }}
                  disabled={busyId !== null}
                  className="rounded-lg bg-white px-4 py-2 text-sm text-slate-700"
                >
                  {t("common.cancel", "Annuler")}
                </button>
              </div>
            </div>
          )}

          {error && (editingId === comment.id || confirmId === comment.id) && (
            <p role="alert" className="mt-2 text-sm text-red-700">
              {error}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default CommentList;
